import { Badge, Card, Heading, IconButton, Input, LuIcon, Menu, Separator, Text, toast } from "@overmap-ai/blocks"
import {
	IssuePriorityType,
	type IssueType,
	type IssueTypePriority,
	OrganizationAccessLevel,
	type Stored,
	ISSUE_PRIORITY_TYPES,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import { APIError, selectIssueTypeById, selectIssueTypePrioritiesOfIssueType } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback, useMemo } from "react"

import { ColorButton } from "../../components/Buttons/ColorButton"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { CreateIssueTypePriorityDialog } from "../../components/Dialogs/CreateIssueTypePriorityDialog"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { ColorPickerPopover } from "../../components/Popovers/ColorPickerPopover"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput, useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { issuePriorityTypeData } from "../../mappings/issues"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/issue-types/$issueTypeId/priorities")

export const IssueTypeSettingsPriorityView = memo(() => {
	const { issueTypeId } = routeApi.useParams()
	const issueType = useAppSelector(selectIssueTypeById(issueTypeId))

	if (!issueType) {
		throw new Error(`expected issueType to exist, got ${issueType}.`)
	}

	const issueTypePriorities = useShallowEqaulSelector(selectIssueTypePrioritiesOfIssueType(issueType.uuid))

	const prioritiesByType = useMemo(() => {
		const grouped = new Map<IssuePriorityType, Stored<IssueTypePriority>[]>()

		ISSUE_PRIORITY_TYPES.forEach((type) => grouped.set(type, []))

		issueTypePriorities.forEach((priority) => {
			grouped.get(priority.type)?.push(priority)
		})

		return grouped
	}, [issueTypePriorities])

	const handleSubmit = useCallback(
		(values: Pick<IssueTypePriority, "color" | "name" | "type">) => {
			const existingPrioritiesOfType = prioritiesByType.get(values.type) ?? []
			const nextOrder = existingPrioritiesOfType.length

			const [, promise] = sdk.issueTypePriorities.add({
				issue_type: issueType.uuid,
				name: values.name,
				color: values.color,
				type: values.type,
				order: nextOrder,
			})

			promise.catch((error) => {
				toast.error({
					title: "Failed to create priority",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[issueType.uuid, prioritiesByType],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">Priorities</Heading>
					<Text size="sm" accentColor="base">
						Manage issue type priorities.
					</Text>
				</div>

				<Separator size="full" />

				<Card className="flex flex-col gap-4" size="sm">
					{ISSUE_PRIORITY_TYPES.map((priorityType) => {
						const priorities = prioritiesByType.get(priorityType) ?? []
						const { color, label } = issuePriorityTypeData[priorityType]

						return (
							<div key={priorityType} className="flex flex-col gap-4">
								<Card
									variant="soft"
									className="px-4! flex items-center justify-between gap-2"
									size="xs"
								>
									<Text accentColor="base">{label}</Text>
									<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
										<CreateIssueTypePriorityDialog
											onSubmit={handleSubmit}
											defaultColor={color}
											priorityType={priorityType}
											size="sm"
										>
											<IconButton variant="ghost" size="sm" accentColor="base">
												<LuIcon icon={modelIconMapping.add} />
											</IconButton>
										</CreateIssueTypePriorityDialog>
									</OrganizationAccessRequired>
								</Card>

								{priorities.length > 0 && (
									<ol className="flex flex-col gap-4">
										{priorities.map((issueTypePriority) => (
											<IssueTypePriorityListItem
												key={issueTypePriority.uuid}
												issueTypePriority={issueTypePriority}
												issueType={issueType}
											/>
										))}
									</ol>
								)}
							</div>
						)
					})}
				</Card>
			</section>
		</ViewContainer>
	)
})
IssueTypeSettingsPriorityView.displayName = "IssueTypeSettingsPriorityView"

interface IssueTypePriorityListItemProps {
	issueTypePriority: Stored<IssueTypePriority>
	issueType: Stored<IssueType>
}

const IssueTypePriorityListItem = memo<IssueTypePriorityListItemProps>((props) => {
	const { issueTypePriority, issueType } = props

	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const isAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)
	const isDefault = issueType.default_priority === issueTypePriority.uuid
	const typeData = issuePriorityTypeData[issueTypePriority.type]

	const handleNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.value

			if (issueTypePriority.name === value) return

			const [, promise] = sdk.issueTypePriorities.update({
				uuid: issueTypePriority.uuid,
				name: e.target.value,
			})

			promise.catch((error) => {
				toast.error({
					title: "Failed to update priority",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[issueTypePriority.uuid, issueTypePriority.name],
	)

	const nameInputProps = useBlurInput<HTMLInputElement>(issueTypePriority.name, handleNameInputBlur, true)

	const handleColorChange = useCallback(
		(color: string) => {
			const [, promise] = sdk.issueTypePriorities.update({
				uuid: issueTypePriority.uuid,
				color: color,
			})

			promise.catch((error) => {
				toast.error({
					title: "Failed to update priority color",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[issueTypePriority.uuid],
	)

	const handleSetDefault = useCallback(() => {
		const [, promise] = sdk.issueTypes.update({
			uuid: issueType.uuid,
			default_priority: issueTypePriority.uuid,
		})

		promise.catch((error) => {
			toast.error({
				title: "Failed to set default priority",
				description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
			})
		})
	}, [issueType.uuid, issueTypePriority.uuid])

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete priority",
			description: "The priority will be deleted immediately. This action cannot be undone.",
			onAction: () =>
				sdk.issueTypePriorities.delete(issueTypePriority.uuid).catch((error) => {
					toast.error({
						title: "Failed to delete priority",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				}),
		})
	}, [issueTypePriority.uuid, openConfirmDeleteDialog])

	return (
		<li className="flex items-center justify-between gap-2">
			<div className="flex grow items-center gap-2">
				<ColorPickerPopover
					required
					color={issueTypePriority.color}
					onColorChange={handleColorChange}
					size="sm"
				>
					<ColorButton disabled={!isAdmin} color={`${issueTypePriority.color}40`}>
						<LuIcon icon={typeData.icon} color={issueTypePriority.color} />
					</ColorButton>
				</ColorPickerPopover>

				<Input.Root variant="outline" className="grow">
					<Input.Field placeholder="Enter a name" disabled={!isAdmin} {...nameInputProps} />
				</Input.Root>
			</div>

			<div className="flex items-center gap-1">
				{isDefault && (
					<Badge variant="soft" size="sm" accentColor="base">
						Default
					</Badge>
				)}

				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<Menu.Root align="end">
						<Menu.ClickTrigger>
							<IconButton accentColor="base" variant="ghost" size="sm">
								<LuIcon icon={modelIconMapping.more} />
							</IconButton>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.Item onSelect={handleSetDefault}>
								<LuIcon icon={modelIconMapping.copy} />
								Make default
							</Menu.Item>
							<Menu.Separator />
							<Menu.Item onSelect={handleDelete}>
								<LuIcon icon={modelIconMapping.delete} /> Delete
							</Menu.Item>
						</Menu.Content>
					</Menu.Root>
				</OrganizationAccessRequired>
			</div>
		</li>
	)
})
IssueTypePriorityListItem.displayName = "IssueTypePriorityListItem"
